import { Id, toast } from "react-toastify";

import {
  DuplicateProductType,
  ExperienceDto,
  RedirectDto,
  ShopifyJobType,
  WebsocketEventProgress,
} from "@intelligems/ig-types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { RedirectToPluginDialogProps } from "../../components/Dialogs/RedirectToPluginDialog";
import { GraphConfig } from "../../domains/Dashboard/Graphs/Utils/graphConfigs";
import { VariationFe } from "../../types/schemas/variations";
import { IAppState } from "../types/store";

type WebsocketToast = Record<
  string, // experiment id
  Record<
    ShopifyJobType,
    {
      toast?: Id;
      progress: number;
    }
  >
>;

export interface CheckoutScriptDialogProps {
  code: string;
  isTest: boolean;
}

export interface IGlobalState {
  confetti: boolean;
  progresses: WebsocketToast;
  ignoreUpdateDetected: boolean;
  dialogs: {
    warningBase: {
      isOpen: boolean;
      props?: any;
    };
    onsiteEditsSave: { isOpen: boolean; props?: any };
    onsiteEditRefresh: { isOpen: boolean; props?: any };
    redirectToPluginDialog: {
      isOpen: boolean;
      props?: RedirectToPluginDialogProps;
    };
    setDomain: { isOpen: boolean; props?: any };
    largeDevicePreview: { isOpen: boolean; props?: any };
    billingStatus: { isOpen: boolean; props?: ExperienceDto };
    createExperiment: {
      isOpen: boolean;
      props?: { hideBackButton?: boolean; showContentSection?: boolean };
    };
    setDuplicateProductStatus: { isOpen: boolean; props?: any };
    shippingProgressBar: { isOpen: boolean; props?: any };
    shippingRates: { isOpen: boolean; props?: any };
    priceUpdater: {
      isOpen: boolean;
      props?: { action: "pause" | "end" | "start" | "none" };
    };
    marketPriceUpdater: {
      isOpen: boolean;
      props?: { action: "pause" | "end" | "start" | "none" };
    };
    marketUpdateConfirm: {
      isOpen: boolean;
      props?: {
        action: "pause" | "end" | "start" | "none";
        testGroupId?: string;
      };
    };
    switchAudienceType: {
      isOpen: boolean;
      props: {
        newTab: number;
      };
    };
    switchExpressionType: {
      isOpen: boolean;
      props: {
        newTab: number;
        queryParamIndex: number;
      };
    };
    checkoutScript: { isOpen: boolean; props?: CheckoutScriptDialogProps };
    campaignTestCreateNew: { isOpen: boolean; props?: any };
    scriptTag: { isOpen: boolean; props?: any };
    duplicateProductErrors: { isOpen: boolean; props?: DuplicateProductType };
    bulkSyncDuplicates: { isOpen: boolean; props?: any };
    downloadPriceCsv: { isOpen: boolean; props?: any };
    devicePreviewPath: { isOpen: boolean; props?: any };
    updatedDetected: { isOpen: boolean; props?: any };
    missingShopifyScope: {
      isOpen: boolean;
      props?: {
        initial?: boolean;
      };
    };
    confirmExperimentDeletion: {
      isOpen: boolean;
      props?: any;
    };
    chooseContentTestType: {
      isOpen: boolean;
      props?: any;
    };
    scoped: {
      isOpen: boolean;
      props?: {
        title: string;
        message: string;
        yesButtonText: string;
        yesButtonAction: any;
        noButtonText: string;
        noButtonAction: any;
        showButtons: boolean;
      };
    };
    priceTemplateDownload: { isOpen: boolean; props?: any };
    productPicker: { isOpen: boolean; props?: any };
    autoFillProductPrices: { isOpen: boolean; props?: any };
    scheduleTest: {
      isOpen: boolean;
      props?: { experimentId: string; experimentName: string };
    };
    onboarding: { isOpen: boolean; props?: any };
    productTableWarning: { isOpen: boolean; props?: any };
    deleteDiscountTier: { isOpen: boolean; props?: any };
    dashboardFilters: { isOpen: boolean; props?: any };
    editTestGroups: { isOpen: boolean; props?: any };
    testSave: { isOpen: boolean; props?: any };
    startTest: { isOpen: boolean; props?: { title: string; text: string } };
    duplicateProductNotice: { isOpen: boolean; props?: any };
    endShippingTest: { isOpen: boolean; props?: any };
    previewTestGroupCode: {
      isOpen: boolean;
      props?: {
        id: string;
        path?: string;
        themeId?: string;
      };
    };
    previewExperiment: {
      isOpen: boolean;
      props?: {
        id: string;
        path?: string;
        themeId?: string;
      };
    };
    dataExport: { isOpen: boolean; props?: any };
    groupWinners: { isOpen: boolean; props?: any };
    analyticsViewType: { isOpen: boolean; props?: any };
    rateBuilder: { isOpen: boolean; props?: any };
    noRateSelect: { isOpen: boolean; props?: any };
    createShippingError: { isOpen: boolean; props?: any };
    startShippingError: { isOpen: boolean; props?: any };
    thirdPartyCarrierWarning: { isOpen: boolean; props?: any };
    adminOverrideStartExp: { isOpen: boolean; props?: any };
    discountApplicationTypeSwitch: { isOpen: boolean; props?: any };
    showUserInfoPopup: { isOpen: boolean; props?: any };
    discountGwpVariant: {
      isOpen: boolean;
      props?: { productId: string | null };
    };
    redirectAssetQuickView: { isOpen: boolean; props?: any };
    templateSnippetGate: { isOpen: boolean; props?: any };
    experimentInfo: { isOpen: boolean; props?: any };
    discardTestChanges: { isOpen: boolean; props?: any };
    insufficientPlan: { isOpen: boolean; props?: any };
    upgradeToActivate: { isOpen: boolean; props?: any };
    createPersonalization: {
      isOpen: boolean;
      props?: { dontNavigate?: boolean };
    };
    confirmIgChecklist: {
      isOpen: boolean;
      props?: { isAdminOverride?: boolean };
    };
  };
  drawers: {
    widgetAction: { isOpen: boolean; props?: any };
    campaign: { isOpen: boolean; props?: any };
    history: { isOpen: boolean; props: { type: "experiment" | "campaign" } };
    pageTargeting: { isOpen: boolean; props?: any };
    audienceTargeting: { isOpen: boolean; props?: any };
    advancedAudienceTargeting: {
      isOpen: boolean;
      props?: { queryParamIndex: number };
    };
    redirectAssetSelect: {
      isOpen: boolean;
      props?: {
        collectionsAndProductsData: any;
        testGroup: VariationFe;
        redirectState: RedirectDto;
      };
    };
    sitewideAnalyticsViewDetails: {
      isOpen: boolean;
      props?: { config: GraphConfig };
    };
    exclusionGroups: { isOpen: boolean; props?: any };
    orderExamples: {
      isOpen: boolean;
      props?: { data: any };
    };
  };
}

export const initialScopedProps = {
  title: "Oops! Something went wrong!",
  message: "Please contact support@intelligems.io for assistance.",
  yesButtonText: "Contact Support",
  yesButtonAction: "yes",
  noButtonAction: "howdy",
  noButtonText: "Close",
  showButtons: true,
};

const initialGlobalState: IGlobalState = {
  confetti: false,
  progresses: {},
  ignoreUpdateDetected: false,
  dialogs: {
    warningBase: { isOpen: false },
    setDomain: { isOpen: false },
    campaignTestCreateNew: { isOpen: false },
    redirectToPluginDialog: { isOpen: false },
    billingStatus: { isOpen: false, props: undefined },
    onsiteEditsSave: { isOpen: false, props: undefined },
    updatedDetected: { isOpen: false },
    createExperiment: { isOpen: false },
    devicePreviewPath: { isOpen: false },
    largeDevicePreview: { isOpen: false },
    scriptTag: { isOpen: false },
    missingShopifyScope: { isOpen: false },
    setDuplicateProductStatus: { isOpen: false },
    duplicateProductNotice: { isOpen: false, props: { title: "", text: "" } },
    shippingProgressBar: { isOpen: false },
    chooseContentTestType: { isOpen: false },
    upgradeToActivate: { isOpen: false },
    onsiteEditRefresh: { isOpen: false },
    shippingRates: { isOpen: false },
    priceUpdater: { isOpen: false, props: { action: "none" } },
    marketPriceUpdater: { isOpen: false, props: { action: "none" } },
    marketUpdateConfirm: { isOpen: false, props: { action: "none" } },
    downloadPriceCsv: { isOpen: false },
    checkoutScript: { isOpen: false },
    confirmExperimentDeletion: { isOpen: false },
    duplicateProductErrors: { isOpen: false },
    bulkSyncDuplicates: { isOpen: false },
    scoped: {
      isOpen: false,
      props: initialScopedProps,
    },
    priceTemplateDownload: { isOpen: false },
    productPicker: { isOpen: false },
    onboarding: { isOpen: false },
    autoFillProductPrices: { isOpen: false },
    productTableWarning: { isOpen: false },
    deleteDiscountTier: { isOpen: false },
    dashboardFilters: { isOpen: false },
    editTestGroups: { isOpen: false },
    testSave: { isOpen: false },
    startTest: { isOpen: false },
    endShippingTest: { isOpen: false },
    previewExperiment: { isOpen: false },
    previewTestGroupCode: { isOpen: false },
    dataExport: { isOpen: false },
    groupWinners: { isOpen: false },
    scheduleTest: { isOpen: false, props: undefined },
    analyticsViewType: { isOpen: false },
    rateBuilder: { isOpen: false },
    noRateSelect: { isOpen: false },
    createShippingError: { isOpen: false },
    startShippingError: { isOpen: false },
    thirdPartyCarrierWarning: { isOpen: false },
    adminOverrideStartExp: { isOpen: false },
    discountApplicationTypeSwitch: { isOpen: false },
    showUserInfoPopup: { isOpen: false },
    discountGwpVariant: { isOpen: false, props: { productId: null } },
    redirectAssetQuickView: { isOpen: false },
    templateSnippetGate: { isOpen: false },
    experimentInfo: { isOpen: false },
    discardTestChanges: { isOpen: false },
    insufficientPlan: { isOpen: false },
    createPersonalization: { isOpen: false },
    confirmIgChecklist: { isOpen: false },
    switchAudienceType: {
      isOpen: false,
      props: {
        newTab: 0,
      },
    },
    switchExpressionType: {
      isOpen: false,
      props: {
        newTab: 0,
        queryParamIndex: 0,
      },
    },
  },
  drawers: {
    widgetAction: { isOpen: false },
    campaign: { isOpen: false },
    history: { isOpen: false, props: { type: "experiment" } },
    pageTargeting: { isOpen: false },
    audienceTargeting: { isOpen: false },
    advancedAudienceTargeting: {
      isOpen: false,
      props: {
        queryParamIndex: 0,
      },
    },
    redirectAssetSelect: { isOpen: false },
    sitewideAnalyticsViewDetails: { isOpen: false },
    exclusionGroups: { isOpen: false },
    orderExamples: { isOpen: false },
  },
};

const progressTypeMap: Record<ShopifyJobType, string> = {
  productProgress: "Saving Products",
  removeProductsFromCollection: "Removing Duplicates From Collections",
};

const getProgressName = (
  experimentName: string,
  progressType: ShopifyJobType,
  currentCount?: number,
  totalCount?: number
) => {
  const text = `${progressTypeMap[progressType]} (${experimentName})`;
  if (currentCount && totalCount) {
    return `${text} (${currentCount}/${totalCount})`;
  }
  return text;
};

const slice = createSlice({
  name: "global",
  initialState: initialGlobalState,
  reducers: {
    setIgnoreUpdateDetected: (global, value: PayloadAction<boolean>) => {
      global.ignoreUpdateDetected = value.payload;
    },
    setConfetti: (global, value: PayloadAction<boolean>) => {
      global.confetti = value.payload;
    },
    clearGlobal: (global) => {
      Object.assign(global, initialGlobalState);
    },
    setEventProgress: (
      global,
      value: PayloadAction<Omit<WebsocketEventProgress, "type">>
    ) => {
      try {
        if (!(value.payload.experienceId in global.progresses)) {
          // @ts-ignore
          global.progresses[value.payload.experienceId] = {};
        }

        const newPercent = Math.max(
          global.progresses[value.payload.experienceId][value.payload.event]
            ?.progress || 0,
          value.payload.progress
        );

        if (
          !global.progresses[value.payload.experienceId][value.payload.event]
        ) {
          global.progresses[value.payload.experienceId][value.payload.event] = {
            progress: 0,
          };
        }

        global.progresses[value.payload.experienceId][
          value.payload.event
        ].progress = newPercent;

        if (
          global.progresses[value.payload.experienceId][value.payload.event]
            ?.toast
        ) {
          if (newPercent <= 1) {
            if (value.payload.event !== "productProgress") {
              toast.update(
                global.progresses[value.payload.experienceId][
                  value.payload.event
                ].toast!,
                {
                  render: getProgressName(
                    value.payload.experienceName,
                    value.payload.event,
                    value.payload.currentCount,
                    value.payload.totalCount
                  ),
                  progress: newPercent,
                }
              );
            }
          }
        } else if (value.payload.event !== "productProgress") {
          global.progresses[value.payload.experienceId][value.payload.event] = {
            toast: toast.info(
              getProgressName(
                value.payload.experienceName,
                value.payload.event,
                value.payload.currentCount,
                value.payload.totalCount
              ),
              {
                progress: newPercent,
              }
            ),
            progress: newPercent,
          };
        }
        if (newPercent >= 1) {
          try {
            toast.dismiss(
              global.progresses[value.payload.experienceId][value.payload.event]
                .toast
            );
          } catch (e) {
            // Do nothing
          }
          global.progresses[value.payload.experienceId][
            value.payload.event
          ].progress = 0;
        }
      } catch (e) {
        console.log(e);
        // Do nothing
      }
    },
    setDialog: (
      global,
      value: PayloadAction<{
        dialog: keyof IGlobalState["dialogs"];
        open: boolean;
        props?: any;
      }>
    ) => {
      global.dialogs[value.payload.dialog].isOpen = value.payload.open;
      global.dialogs[value.payload.dialog].props = value.payload.props;
    },
    setDrawer: (
      global,
      value: PayloadAction<{
        drawer: keyof IGlobalState["drawers"];
        open: boolean;
        props?: any;
      }>
    ) => {
      global.drawers[value.payload.drawer].isOpen = value.payload.open;
      global.drawers[value.payload.drawer].props = value.payload.props;
    },
  },
});

export const {
  setIgnoreUpdateDetected,
  setConfetti,
  setDialog,
  clearGlobal,
  setEventProgress,
  setDrawer,
} = slice.actions;

export const isDialogOpen =
  (dialog: keyof IGlobalState["dialogs"]) => (state: IAppState) => {
    return state.global.dialogs[dialog].isOpen;
  };

export const getDialogPropDetails =
  (dialog: keyof IGlobalState["dialogs"]) => (state: IAppState) => {
    return state.global.dialogs[dialog]?.props;
  };

export const getDialog =
  (dialog: keyof IGlobalState["dialogs"]) => (state: IAppState) => {
    return state.global.dialogs[dialog];
  };

export const isDrawerOpen =
  (drawer: keyof IGlobalState["drawers"]) => (state: IAppState) => {
    return state.global.drawers[drawer].isOpen;
  };

export const getIgnoreUpdateDetected = (state: IAppState) => {
  return state.global.ignoreUpdateDetected;
};

export const getDrawerPropDetails =
  (drawer: keyof IGlobalState["drawers"]) => (state: IAppState) => {
    return state.global.drawers[drawer]?.props;
  };

export const getDrawer =
  (drawer: keyof IGlobalState["drawers"]) => (state: IAppState) => {
    return state.global.drawers[drawer];
  };

export const getProgresses = () => (state: IAppState) => {
  return state.global.progresses;
};

export const globalReducer = slice.reducer;
